import { useState } from "react";
import { Send, User } from "lucide-react";

import {
  MockConversationLine,
  MockGenieConfig,
  WizardStepId,
} from "@/types/mockups/genie-setup-v2";

import { simulateResponse, sampleTestPrompts } from "../composer";
import { GenieIcon } from "@/components/ui/genie-icons/GenieIcon";

interface Step5PreviewProps {
  config: MockGenieConfig;
  onJumpToStep: (stepId: WizardStepId) => void;
}

export const Step5Preview = ({ config, onJumpToStep }: Step5PreviewProps) => {
  const [conversation, setConversation] = useState<MockConversationLine[]>([]);
  const [input, setInput] = useState("");

  const sendMessage = (message: string) => {
    if (!message.trim()) return;
    setConversation((prev) => [
      ...prev,
      { speaker: "user", message },
      { speaker: "agent", message: simulateResponse(message, config) },
    ]);
    setInput("");
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-5 gap-5">
      {/* Chat panel — gradient border */}
      <div className="lg:col-span-3 p-[2px] rounded-2xl bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8]">
        <div className="rounded-[14px] bg-white flex flex-col h-[520px] overflow-hidden">
          <div className="px-5 py-3 border-b border-gray-100 flex items-center gap-3">
            <GenieIcon name="genie-lamp" size={32} className="h-8 w-8" />
            <div className="flex-1">
              <p className="text-sm font-semibold text-gray-900">Test conversation</p>
              <p className="text-xs text-gray-500">Responses match your current settings</p>
            </div>
            {conversation.length > 0 && (
              <button
                type="button"
                onClick={() => setConversation([])}
                className="text-xs text-genie-primary hover:underline"
              >
                Reset
              </button>
            )}
          </div>

          <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3">
            {conversation.length === 0 ? (
              <div className="h-full flex flex-col items-center justify-center text-center text-gray-400 px-6 gap-3">
                <GenieIcon name="chat" size={40} className="h-10 w-10 opacity-60" />
                <p className="text-sm">
                  Send a test message or pick a sample on the right to see how your genie
                  responds.
                </p>
              </div>
            ) : (
              conversation.map((line, i) => (
                <div
                  key={i}
                  className={`flex gap-2 ${line.speaker === "user" ? "justify-end" : "justify-start"}`}
                >
                  {line.speaker === "agent" && (
                    <GenieIcon
                      name="genie-lamp"
                      size={28}
                      className="w-7 h-7 shrink-0 mt-0.5"
                    />
                  )}
                  <div
                    className={`max-w-[80%] rounded-2xl px-3.5 py-2 text-sm ${
                      line.speaker === "user"
                        ? "text-white"
                        : "bg-gray-100 text-gray-900"
                    }`}
                    style={
                      line.speaker === "user"
                        ? {
                            background:
                              "linear-gradient(90deg, #4E9CFF 0%, #FF4DB8 100%)",
                          }
                        : undefined
                    }
                  >
                    {line.message}
                  </div>
                  {line.speaker === "user" && (
                    <div className="w-7 h-7 rounded-full bg-gray-200 text-gray-600 flex items-center justify-center shrink-0">
                      <User className="w-3.5 h-3.5" />
                    </div>
                  )}
                </div>
              ))
            )}
          </div>

          <form
            onSubmit={(e) => {
              e.preventDefault();
              sendMessage(input);
            }}
            className="border-t border-gray-100 p-3 flex items-center gap-2"
          >
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Type a sample message..."
              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-genie-primary bg-white text-sm"
            />
            <button
              type="submit"
              disabled={!input.trim()}
              className="w-9 h-9 rounded-lg text-white flex items-center justify-center disabled:opacity-40 disabled:cursor-not-allowed shadow-md hover:opacity-90 transition-opacity"
              style={{ background: "linear-gradient(90deg, #4E9CFF 0%, #FF4DB8 100%)" }}
            >
              <Send className="w-4 h-4" />
            </button>
          </form>
        </div>
      </div>

      <div className="lg:col-span-2 space-y-4">
        <div className="bg-white rounded-2xl border border-gray-200 p-5">
          <h3 className="text-sm font-semibold text-gray-900 mb-3">Try a sample</h3>
          <div className="space-y-2">
            {sampleTestPrompts.map((prompt) => (
              <button
                key={prompt}
                type="button"
                onClick={() => sendMessage(prompt)}
                className="w-full text-left text-sm px-3 py-2.5 rounded-lg border border-gray-200 hover:border-genie-primary hover:bg-genie-primary/5 text-gray-700 transition-colors"
              >
                "{prompt}"
              </button>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-gray-200 p-5">
          <h3 className="text-sm font-semibold text-gray-900 mb-1">Not quite right?</h3>
          <p className="text-xs text-gray-600 mb-3">
            Each dial is one click away. Jump back, change it, come back.
          </p>
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => onJumpToStep("personality")}
              className="text-xs font-medium px-3 py-2 rounded-lg bg-white border border-gray-200 hover:border-genie-primary hover:text-genie-primary text-gray-700 transition-colors"
            >
              Adjust personality
            </button>
            <button
              type="button"
              onClick={() => onJumpToStep("job")}
              className="text-xs font-medium px-3 py-2 rounded-lg bg-white border border-gray-200 hover:border-genie-primary hover:text-genie-primary text-gray-700 transition-colors"
            >
              Change job
            </button>
            <button
              type="button"
              onClick={() => onJumpToStep("playbooks")}
              className="text-xs font-medium px-3 py-2 rounded-lg bg-white border border-gray-200 hover:border-genie-primary hover:text-genie-primary text-gray-700 transition-colors"
            >
              Edit playbooks
            </button>
            <button
              type="button"
              onClick={() => onJumpToStep("knowledge")}
              className="text-xs font-medium px-3 py-2 rounded-lg bg-white border border-gray-200 hover:border-genie-primary hover:text-genie-primary text-gray-700 transition-colors"
            >
              Update knowledge
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
